import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL, resolveUploadUrl } from "../config";
import { Link } from 'react-router-dom';
import { 
  FaClipboardList, FaCheckCircle, FaTruck, FaClock, 
  FaDollarSign, FaStar, FaChartLine,
  FaCalendarAlt, FaArrowRight, FaUser, FaPlusCircle,
  FaHistory, FaEnvelope, FaBell, FaThumbsUp,
  FaBuilding, FaBox, FaMapMarkerAlt, FaPhone
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';

function SubbieDashboard({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    newJobs: 0,
    activeJobs: 0,
    completedJobs: 0,
    totalEarnings: 0,
    totalJobs: 0,
    fleetCount: 0,
    rating: 0,
    totalReviews: 0
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [user, setUser] = useState({});
  const [companyName, setCompanyName] = useState('');
  const [subbie, setSubbie] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchDashboardData(userData);
  }, []);

  const fetchDashboardData = async (userData) => {
    setLoading(true);
    try {
      // First, get the subcontractor ID
      let subbieId = localStorage.getItem('subbieId');
      
      if (!subbieId) {
        const response = await fetch(`/subcontractors?user_id=${userData.id}`);
        const data = await response.json();
        if (data.success && data.subcontractors && data.subcontractors.length > 0) {
          const id = data.subcontractors[0].id;
          localStorage.setItem('subbieId', id);
          setCompanyName(data.subcontractors[0].company_name || '');
          setSubbie(data.subcontractors[0] || null);
          await fetchAllData(id);
        }
      } else {
        // Get company name
        const response = await fetch(`/subcontractors/${subbieId}`);
        const data = await response.json();
        if (data.success) {
          setCompanyName(data.subcontractor?.company_name || '');
          setSubbie(data.subcontractor || null);
        }
        await fetchAllData(subbieId);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setLoading(false);
    }
  };

  const fetchAllData = async (subbieId) => {
    try {
      // Fetch all job requirements for this subbie
      const response = await fetch(`/subbie/all-requirements/${subbieId}`);
      const data = await response.json();
      
      if (data.success) {
        const requirements = data.requirements || [];
        
        // Calculate stats from REAL data
        const newJobs = requirements.filter(r => r.status === 'pending' || r.status === 'quoting').length;
        const activeJobs = requirements.filter(r => r.status === 'approved' || r.status === 'accepted' || r.status === 'assigned').length;
        const completedJobs = requirements.filter(r => r.status === 'completed' || r.status === 'closed').length;
        const totalJobs = requirements.length;
        
        // Calculate total earnings from completed jobs
        let totalEarnings = 0;
        requirements.forEach(r => {
          if ((r.status === 'completed' || r.status === 'closed') && r.jobs?.subbie_target_price && r.jobs?.total_quantity_mt) {
            totalEarnings += (r.jobs.total_quantity_mt || 0) * (r.jobs?.subbie_target_price || 0);
          }
        });

        // Get recent jobs (last 5)
        const recent = requirements.slice(0, 5);

        // Get upcoming jobs (active jobs)
        const upcoming = requirements
          .filter(r => r.status === 'approved' || r.status === 'accepted' || r.status === 'assigned')
          .slice(0, 5);

        // Get fleet count
        let fleetCount = 0;
        try {
          const fleetResponse = await fetch(`/subbie/fleet/${subbieId}`);
          const fleetData = await fleetResponse.json();
          if (fleetData.success) {
            fleetCount = fleetData.trucks?.length || 0;
          }
        } catch (fleetErr) {
          console.error('Error fetching fleet:', fleetErr);
        }

        // Get rating from reviews
        let rating = 0;
        let totalReviews = 0;
        try {
          const reviewsResponse = await fetch(`/reviews/subcontractor/${subbieId}`);
          const reviewsData = await reviewsResponse.json();
          if (reviewsData.success && reviewsData.reviews) {
            totalReviews = reviewsData.reviews.length;
            if (totalReviews > 0) {
              const sum = reviewsData.reviews.reduce((acc, r) => acc + r.rating, 0);
              rating = Math.round((sum / totalReviews) * 10) / 10;
            }
          }
        } catch (reviewErr) {
          console.error('Error fetching reviews:', reviewErr);
        }

        setStats({
          newJobs,
          activeJobs,
          completedJobs,
          totalEarnings,
          totalJobs,
          fleetCount,
          rating,
          totalReviews
        });

        setRecentJobs(recent);
        setUpcomingJobs(upcoming);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'quoting': 'bg-blue-100 text-blue-600',
      'approved': 'bg-green-100 text-green-600',
      'accepted': 'bg-green-100 text-green-600',
      'assigned': 'bg-purple-100 text-purple-600',
      'completed': 'bg-gray-100 text-gray-600',
      'closed': 'bg-gray-100 text-gray-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Awaiting Response',
      'quoting': 'Quote Submitted',
      'approved': 'Approved',
      'accepted': 'Accepted',
      'assigned': 'Fleet Assigned',
      'completed': 'Completed',
      'closed': 'Closed'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <SubbieLayout activeTab="dashboard" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading dashboard...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  return (
    <SubbieLayout activeTab="dashboard" onLogout={onLogout} user={user}>
      {/* Welcome Section */}
      <div className="rounded-2xl p-8 mb-8" style={{ backgroundColor: '#0A1929' }}>
        <div className="flex flex-wrap justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0" style={{ border: '2px solid rgba(255,255,255,0.15)' }}>
              {subbie?.company_logo_url ? (
                <img src={resolveUploadUrl(subbie.company_logo_url)} alt="Company logo" className="w-full h-full object-contain" />
              ) : (
                <span className="text-2xl font-bold text-white">{String(companyName || user?.name || 'S').charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                👋 Welcome back, {companyName || user?.name || 'Subcontractor'}!
              </h1>
              <p className="mt-1" style={{ color: '#94A3B8' }}>Here's your work overview</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link 
              to="/subbie-new-jobs"
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-white" 
              style={{ backgroundColor: '#00A86B' }}
            >
              <FaPlusCircle /> Find Jobs
            </Link>
          </div>
        </div>
      </div>

      {/* ✅ COMPLETE YOUR PROFILE BANNER */}
      {(() => {
        const missing = [];
        if (!subbie?.company_name) missing.push('Company Name');
        if (!subbie?.registration_number) missing.push('Company Reg');
        if (!subbie?.contact_person) missing.push('Contact');
        if (!subbie?.contact_phone) missing.push('Phone');
        if (!subbie?.contact_email) missing.push('Email');
        if (!subbie?.bank_name || !subbie?.bank_account_number || !subbie?.bank_branch_code) missing.push('Banking Details');
        return missing;
      })().length > 0 && (
        <div className="rounded-xl p-6 mb-6 border-2" style={{ backgroundColor: '#EBF5FF', borderColor: '#3B82F6' }}>
          <div className="flex items-start gap-4">
            <div className="text-3xl">📋</div>
            <div className="flex-1">
              <h3 className="font-bold text-lg" style={{ color: '#1E40AF' }}>Complete Your Profile</h3>
              <p style={{ color: '#1E3A8A' }}>
                Please update your profile with:{' '}
                <strong>{(() => {
                  const m = [];
                  if (!subbie?.company_name) m.push('Company Name');
                  if (!subbie?.registration_number) m.push('Company Reg');
                  if (!subbie?.contact_person) m.push('Contact');
                  if (!subbie?.contact_phone) m.push('Phone');
                  if (!subbie?.contact_email) m.push('Email');
                  if (!subbie?.bank_name || !subbie?.bank_account_number || !subbie?.bank_branch_code) m.push('Banking Details');
                  return m.join(', ');
                })()}</strong>
              </p>
              <Link
                to="/subbie-profile"
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#3B82F6' }}
              >
                Complete Profile <FaArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* New Jobs */}
        <div className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: '#4A5568' }}>New Jobs</p>
              <p className="text-2xl font-bold" style={{ color: '#0A1929' }}>{stats.newJobs}</p>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
              <FaClipboardList className="text-xl" style={{ color: '#F59E0B' }} />
            </div>
          </div>
          <Link 
            to="/subbie-new-jobs"
            className="mt-3 text-sm flex items-center gap-1" style={{ color: '#00A86B' }}
          >
            View jobs <FaArrowRight size={12} />
          </Link>
        </div>

        {/* Active Jobs */}
        <div className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: '#4A5568' }}>Active Jobs</p>
              <p className="text-2xl font-bold" style={{ color: '#0A1929' }}>{stats.activeJobs}</p>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#DBEAFE' }}>
              <FaTruck className="text-xl" style={{ color: '#3B82F6' }} />
            </div>
          </div>
          <Link 
            to="/subbie-active-jobs"
            className="mt-3 text-sm flex items-center gap-1" style={{ color: '#00A86B' }}
          >
            View jobs <FaArrowRight size={12} />
          </Link>
        </div>

        {/* Completed Jobs */}
        <div className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: '#4A5568' }}>Completed</p>
              <p className="text-2xl font-bold" style={{ color: '#0A1929' }}>{stats.completedJobs}</p>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#D1FAE5' }}>
              <FaCheckCircle className="text-xl" style={{ color: '#00A86B' }} />
            </div>
          </div>
          <Link 
            to="/subbie-closed-jobs"
            className="mt-3 text-sm flex items-center gap-1" style={{ color: '#00A86B' }}
          >
            View jobs <FaArrowRight size={12} />
          </Link>
        </div>

        {/* Total Earnings */}
        <div className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: '#4A5568' }}>Total Earnings</p>
              <p className="text-2xl font-bold" style={{ color: '#00A86B' }}>R{stats.totalEarnings.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#EDE9FE' }}>
              <FaDollarSign className="text-xl" style={{ color: '#7C3AED' }} />
            </div>
          </div>
          <Link 
            to="/subbie-payments"
            className="mt-3 text-sm flex items-center gap-1" style={{ color: '#00A86B' }}
          >
            View payments <FaArrowRight size={12} />
          </Link>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
              <FaStar className="text-sm" style={{ color: '#F59E0B' }} />
            </div>
            <div>
              <p className="text-xs" style={{ color: '#4A5568' }}>Rating</p>
              <p className="font-bold" style={{ color: '#0A1929' }}>{stats.rating} / 5.0 ⭐</p>
              <p className="text-xs" style={{ color: '#94A3B8' }}>{stats.totalReviews} reviews</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#DBEAFE' }}>
              <FaTruck className="text-sm" style={{ color: '#3B82F6' }} />
            </div>
            <div>
              <p className="text-xs" style={{ color: '#4A5568' }}>Fleet Size</p>
              <p className="font-bold" style={{ color: '#0A1929' }}>{stats.fleetCount} vehicles</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#D1FAE5' }}>
              <FaHistory className="text-sm" style={{ color: '#00A86B' }} />
            </div>
            <div>
              <p className="text-xs" style={{ color: '#4A5568' }}>Total Jobs</p>
              <p className="font-bold" style={{ color: '#0A1929' }}>{stats.totalJobs}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#EDE9FE' }}>
              <FaChartLine className="text-sm" style={{ color: '#7C3AED' }} />
            </div>
            <div>
              <p className="text-xs" style={{ color: '#4A5568' }}>Completion Rate</p>
              <p className="font-bold" style={{ color: '#0A1929' }}>
                {stats.totalJobs > 0 ? Math.round((stats.completedJobs / stats.totalJobs) * 100) : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent & Upcoming Jobs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Jobs */}
        <div className="bg-white rounded-xl border p-6 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <h3 className="font-bold mb-4 flex items-center gap-2" style={{ color: '#0A1929' }}>
            <FaClock style={{ color: '#00A86B' }} /> Recent Jobs
          </h3>
          {recentJobs.length === 0 ? (
            <p className="text-center py-8" style={{ color: '#4A5568' }}>No recent jobs yet</p>
          ) : (
            <div className="space-y-3">
              {recentJobs.map((job, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#F8F9FA' }}>
                  <div>
                    <p className="font-medium text-sm" style={{ color: '#0A1929' }}>{job.jobs?.job_number || 'N/A'}</p>
                    <p className="text-xs" style={{ color: '#4A5568' }}>{job.jobs?.commodity || 'Unknown'} • {job.jobs?.total_quantity_mt || 0} MT</p>
                  </div>
                  <span className={getStatusBadge(job.status)}>
                    {getStatusLabel(job.status)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <Link 
            to="/subbie-active-jobs"
            className="mt-4 text-sm flex items-center gap-1" style={{ color: '#00A86B' }}
          >
            View all jobs <FaArrowRight size={12} />
          </Link>
        </div>

        {/* Upcoming Jobs */}
        <div className="bg-white rounded-xl border p-6 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <h3 className="font-bold mb-4 flex items-center gap-2" style={{ color: '#0A1929' }}>
            <FaCalendarAlt style={{ color: '#00A86B' }} /> Upcoming Jobs
          </h3>
          {upcomingJobs.length === 0 ? (
            <p className="text-center py-8" style={{ color: '#4A5568' }}>No upcoming jobs scheduled</p>
          ) : (
            <div className="space-y-3">
              {upcomingJobs.map((job, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#F8F9FA' }}>
                  <div>
                    <p className="font-medium text-sm" style={{ color: '#0A1929' }}>{job.jobs?.job_number || 'N/A'}</p>
                    <p className="text-xs" style={{ color: '#4A5568' }}>{job.jobs?.origin || 'Unknown'} → {job.jobs?.destination || 'Unknown'}</p>
                  </div>
                  <span className="text-xs" style={{ color: '#94A3B8' }}>
                    {job.jobs?.start_date || 'TBD'}
                  </span>
                </div>
              ))}
            </div>
          )}
          <Link 
            to="/subbie-active-jobs"
            className="mt-4 text-sm flex items-center gap-1" style={{ color: '#00A86B' }}
          >
            View schedule <FaArrowRight size={12} />
          </Link>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Link 
          to="/subbie-new-jobs"
          className="text-white rounded-xl p-4 text-center transition-colors" 
          style={{ backgroundColor: '#00A86B' }}
        >
          <FaClipboardList className="mx-auto mb-2 text-xl" />
          <span className="text-sm font-medium">Find Jobs</span>
        </Link>
        <Link 
          to="/subbie-active-jobs"
          className="text-white rounded-xl p-4 text-center transition-colors" 
          style={{ backgroundColor: '#3B82F6' }}
        >
          <FaTruck className="mx-auto mb-2 text-xl" />
          <span className="text-sm font-medium">Active Jobs</span>
        </Link>
        <Link 
          to="/subbie-fleet"
          className="text-white rounded-xl p-4 text-center transition-colors" 
          style={{ backgroundColor: '#7C3AED' }}
        >
          <FaPlusCircle className="mx-auto mb-2 text-xl" />
          <span className="text-sm font-medium">Manage Fleet</span>
        </Link>
        <Link 
          to="/subbie-profile"
          className="text-white rounded-xl p-4 text-center transition-colors" 
          style={{ backgroundColor: '#0A1929' }}
        >
          <FaUser className="mx-auto mb-2 text-xl" />
          <span className="text-sm font-medium">My Profile</span>
        </Link>
      </div>

      {/* Status Summary */}
      <div className="mt-8 rounded-xl border p-6" style={{ backgroundColor: '#F8F9FA', borderColor: '#E8ECEF' }}>
        <h4 className="font-bold mb-3" style={{ color: '#0A1929' }}>📊 Job Status Summary</h4>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
          <div className="text-center p-3 bg-white rounded-lg border" style={{ borderColor: '#E8ECEF' }}>
            <p className="font-bold text-xl" style={{ color: '#F59E0B' }}>{stats.newJobs}</p>
            <p style={{ color: '#4A5568' }}>New</p>
          </div>
          <div className="text-center p-3 bg-white rounded-lg border" style={{ borderColor: '#E8ECEF' }}>
            <p className="font-bold text-xl" style={{ color: '#3B82F6' }}>{stats.activeJobs}</p>
            <p style={{ color: '#4A5568' }}>Active</p>
          </div>
          <div className="text-center p-3 bg-white rounded-lg border" style={{ borderColor: '#E8ECEF' }}>
            <p className="font-bold text-xl" style={{ color: '#00A86B' }}>{stats.completedJobs}</p>
            <p style={{ color: '#4A5568' }}>Completed</p>
          </div>
          <div className="text-center p-3 bg-white rounded-lg border" style={{ borderColor: '#E8ECEF' }}>
            <p className="font-bold text-xl" style={{ color: '#0A1929' }}>{stats.totalJobs}</p>
            <p style={{ color: '#4A5568' }}>Total</p>
          </div>
          <div className="text-center p-3 bg-white rounded-lg border" style={{ borderColor: '#E8ECEF' }}>
            <p className="font-bold text-xl" style={{ color: '#7C3AED' }}>
              {stats.totalJobs > 0 ? Math.round((stats.completedJobs / stats.totalJobs) * 100) : 0}%
            </p>
            <p style={{ color: '#4A5568' }}>Complete %</p>
          </div>
        </div>
      </div>
    </SubbieLayout>
  );
}

export default SubbieDashboard;
